import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  DollarSign,
  ClipboardList,
  Calendar,
  UserCheck,
  UsersRound,
  FileText,
  Settings,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Landmark,
  Receipt,
  FileSpreadsheet,
  Wallet,
  Menu,
  X,
  CreditCard,
  ShoppingCart,
  Building,
  CalendarCheck
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const menuItems = [
  { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { name: 'Students', icon: Users, path: '/students' },
  { name: 'Classes', icon: GraduationCap, path: '/classes' },
  { name: 'Fees', icon: DollarSign, path: '/fees' },
  { name: 'Exams', icon: ClipboardList, path: '/exams' },
  { name: 'Attendance', icon: Calendar, path: '/attendance' },
  { name: 'Teachers', icon: UserCheck, path: '/teachers' },
  { name: 'Parents', icon: UsersRound, path: '/parents' },
  { name: 'Reports', icon: FileText, path: '/reports' },
];

const financeMenuItems = [
  { name: 'Fee Structures', icon: FileText, path: '/finance/fee-structures' },
  { name: 'Invoices', icon: CreditCard, path: '/finance/invoices' },
  { name: 'Suppliers', icon: ShoppingCart, path: '/finance/suppliers' },
  { name: 'Expenses', icon: Receipt, path: '/finance/expenses' },
  { name: 'Payroll', icon: Wallet, path: '/finance/payroll' },
  { name: 'Chart of Accounts', icon: BookOpen, path: '/finance/accounts' },
  { name: 'Journal Entries', icon: FileSpreadsheet, path: '/finance/journals' },
  { name: 'General Ledger', icon: FileSpreadsheet, path: '/finance/ledger' },
  { name: 'Balance Sheet', icon: Landmark, path: '/finance/balance-sheet' },
  { name: 'Profit & Loss', icon: FileText, path: '/finance/profit-loss' },
  { name: 'Year-End Closing', icon: CalendarCheck, path: '/finance/year-end-closing' },
  { name: 'School Settings', icon: Building, path: '/finance/school-settings' },
];

const NavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-white/10 text-white'
          : 'text-slate-400 hover:bg-white/5 hover:text-white'
      )}
    >
      <Icon className="w-5 h-5" />
      <span>{item.name}</span>
    </Link>
  );
};

const SidebarContent = ({ onItemClick }) => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [financeOpen, setFinanceOpen] = useState(
    location.pathname.startsWith('/finance')
  );

  const isFinancePath = location.pathname.startsWith('/finance');

  return (
    <div className="flex flex-col h-full bg-slate-900">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
          <GraduationCap className="w-6 h-6 text-white" />
        </div>
        <span className="text-xl font-bold text-white tracking-tight">ACADEMION</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            isActive={location.pathname === item.path}
            onClick={onItemClick}
          />
        ))}

        {/* Finance Submenu - Only for Admin */}
        {isAdmin() && (
          <div className="pt-2">
            <button
              onClick={() => setFinanceOpen(!financeOpen)}
              className={cn(
                'w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                isFinancePath
                  ? 'bg-white/10 text-white'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white'
              )}
            >
              <div className="flex items-center gap-3">
                <Landmark className="w-5 h-5" />
                <span>Financials</span>
              </div>
              {financeOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>

            {financeOpen && (
              <div className="mt-1 ml-4 pl-4 border-l border-white/10 space-y-1">
                {financeMenuItems.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    isActive={location.pathname === item.path}
                    onClick={onItemClick}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Settings */}
      <div className="px-3 py-4 border-t border-white/10">
        <NavItem
          item={{ name: 'Settings', icon: Settings, path: '/settings' }}
          isActive={location.pathname === '/settings'}
          onClick={onItemClick}
        />
      </div>
    </div>
  );
};

export const Sidebar = () => {
  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 h-screen fixed left-0 top-0 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900">ACADEMION</span>
          </div>
          
          <Sheet>
            <SheetTrigger asChild>
              <button className="p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6 text-slate-600" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
